import React, { useEffect, useMemo, useState } from 'react';
import { Container, Grid, MenuItem, TextField } from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import { format } from 'date-fns';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import useSettings from '../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { dispatch, useSelector } from '../../../redux/store';
import { getQtyChangeAudit } from '../../../redux/slices/stockManagementRedux/stockRedux';
import { accessVerify, utcMoment } from '../../../utils/common';
import PermissionRequired from '../../errorPages/permissionRequired';

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
};

const DATE_RANGE_MODE = {
  LAST_30_DAYS: 'LAST_30_DAYS',
  TODAY: 'TODAY',
  THIS_MONTH: 'THIS_MONTH',
  ALL_TIME: 'ALL_TIME',
  CUSTOM: 'CUSTOM',
};

const formatFilterDate = (date, time) => `${format(date, 'yyyy-MM-dd')} ${time}`;

const formatDisplayQty = (value, unitSymbol) => {
  if (value === null || value === undefined) return 'Unknown';
  return `${Number(value)}${unitSymbol ? ` ${unitSymbol}` : ''}`;
};

const StockQtyChangeReport = () => {
  const { themeStretch } = useSettings();
  const [dateRangeMode, setDateRangeMode] = useState(DATE_RANGE_MODE.LAST_30_DAYS);
  const [filterStartDate, setFilterStartDate] = useState(getDefaultStartDate());
  const [filterEndDate, setFilterEndDate] = useState(new Date());
  const [stockCode, setStockCode] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { selectedLocation } = useSelector((state) => state.location);
  const { qtyChangeAuditData } = useSelector((state) => state.stock);

  const getDateRangeFilters = () => {
    const today = new Date();

    switch (dateRangeMode) {
      case DATE_RANGE_MODE.TODAY:
        return {
          startDate: formatFilterDate(today, '00:00:00'),
          endDate: formatFilterDate(today, '23:59:59'),
        };
      case DATE_RANGE_MODE.THIS_MONTH:
        return {
          startDate: formatFilterDate(new Date(today.getFullYear(), today.getMonth(), 1), '00:00:00'),
          endDate: formatFilterDate(today, '23:59:59'),
        };
      case DATE_RANGE_MODE.ALL_TIME:
        return {
          startDate: '',
          endDate: '',
        };
      case DATE_RANGE_MODE.CUSTOM:
        return {
          startDate: filterStartDate ? formatFilterDate(filterStartDate, '00:00:00') : '',
          endDate: filterEndDate ? formatFilterDate(filterEndDate, '23:59:59') : '',
        };
      case DATE_RANGE_MODE.LAST_30_DAYS:
      default:
        return {
          startDate: formatFilterDate(getDefaultStartDate(), '00:00:00'),
          endDate: formatFilterDate(today, '23:59:59'),
        };
    }
  };

  useEffect(() => {
    if (selectedLocation?.id) {
      const dateRangeFilters = getDateRangeFilters();
      setIsLoading(true);
      dispatch(
        getQtyChangeAudit({
          startDate: dateRangeFilters.startDate,
          endDate: dateRangeFilters.endDate,
          locationId: selectedLocation.id,
          stockCode: stockCode?.trim(),
        })
      ).finally(() => setIsLoading(false));
    }
  }, [dateRangeMode, filterStartDate, filterEndDate, selectedLocation?.id, stockCode]);

  const tableData = useMemo(
    () =>
      (qtyChangeAuditData || []).map((value) => ({
        stockDateTime: value?.stockDateTime ? utcMoment(value?.stockDateTime).format('YYYY-MM-DD HH:mm:ss') : '-',
        stockCode: value?.stockCode,
        foodName: value?.foodName,
        locationName: value?.locationName,
        userName: value?.userName,
        previousQty: formatDisplayQty(value?.previousQty, value?.unitSymbol),
        newQty: formatDisplayQty(value?.newQty, value?.unitSymbol),
        differenceQty: value?.differenceQty === null || value?.differenceQty === undefined ? 'Unknown' : Number(value?.differenceQty),
        actionType: value?.actionType || 'Unknown',
        description: value?.description,
      })),
    [qtyChangeAuditData]
  );

  const columns = [
    { accessorKey: 'stockDateTime', header: 'Date / Time' },
    { accessorKey: 'stockCode', header: 'Stock Code' },
    { accessorKey: 'foodName', header: 'Food Name' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'userName', header: 'Changed By' },
    { accessorKey: 'previousQty', header: 'Previous Qty' },
    { accessorKey: 'newQty', header: 'New Qty' },
    { accessorKey: 'differenceQty', header: 'Difference' },
    { accessorKey: 'actionType', header: 'Action Type' },
    { accessorKey: 'description', header: 'Description' },
  ];

  return (
    <Page title='Qty Change Report'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Qty Change Report'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Stock', href: PATH_DASHBOARD.stocks.root }, { name: 'Qty Change Report' }]}
          action={[]}
        />

        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12} md={dateRangeMode === DATE_RANGE_MODE.CUSTOM ? 3 : 6}>
            <TextField select label='Date range' value={dateRangeMode} onChange={(event) => setDateRangeMode(event.target.value)} fullWidth>
              <MenuItem value={DATE_RANGE_MODE.LAST_30_DAYS}>Last 30 Days</MenuItem>
              <MenuItem value={DATE_RANGE_MODE.TODAY}>Today</MenuItem>
              <MenuItem value={DATE_RANGE_MODE.THIS_MONTH}>This Month</MenuItem>
              <MenuItem value={DATE_RANGE_MODE.ALL_TIME}>All Time</MenuItem>
              <MenuItem value={DATE_RANGE_MODE.CUSTOM}>Custom Range</MenuItem>
            </TextField>
          </Grid>
          {dateRangeMode === DATE_RANGE_MODE.CUSTOM && (
            <>
              <Grid item xs={12} md={3}>
                <DatePicker label='Start date' value={filterStartDate} onChange={(newValue) => newValue && setFilterStartDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth />} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker label='End date' value={filterEndDate} onChange={(newValue) => newValue && setFilterEndDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth />} />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={dateRangeMode === DATE_RANGE_MODE.CUSTOM ? 3 : 6}>
            <TextField label='Stock code' value={stockCode} onChange={(event) => setStockCode(event.target.value)} fullWidth />
          </Grid>
        </Grid>

        {accessVerify('STOCK_QTY_AUDIT_VIEW') ? (
          <DataGridTable
            name={`${selectedLocation?.name || 'All Locations'} - Stock Qty Change Report - ${dateRangeMode === DATE_RANGE_MODE.ALL_TIME ? 'All Time' : dateRangeMode === DATE_RANGE_MODE.CUSTOM ? `${format(filterStartDate, 'yyyy-MM-dd')} to ${format(filterEndDate, 'yyyy-MM-dd')}` : dateRangeMode}`}
            data={tableData}
            column={columns}
            isLoading={isLoading}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowActions={false}
            enableRowSelection={false}
            isRowClickable={false}
            renderRowActionItems={() => []}
          />
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
};

export default StockQtyChangeReport;
